import { cn } from '@/lib/utils';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { Loading } from '@chiroup/components/Loading';
import { Condition } from '@chiroup/core/types/Condition.type';
import { Muscle } from '@chiroup/core/types/Muscle.type';
import React, { useEffect, useMemo, useState } from 'react';
import { MYOFASCIAL_MUSCLES_NOT_AUTO_SELECT } from '../../../../../constants';
import useMuscles from '../../../../../hooks/useMuscles';
import MuscleAutoCompleteMulti from './MuscleAutoCompleteMulti';
import MyofascialItem from './MyofascialItem';

type Props = {
  close?: () => void;
  value?: {
    [key: string]: { [key: string]: { value?: any } };
  };
  onChange: (
    region: string,
    prop: string,
    val: {
      value?: any;
    }
  ) => void;
  conditions: {
    [key: number]: Partial<Condition>;
  };
  signed?: boolean;
};

const Myofascial: React.FC<Props> = ({ close, value, onChange, conditions, signed }) => {
  const { data: musclesData, isFetching: isFetchingMuscles } = useMuscles({
    refetchOnMount: false,
  });
  const [regions, setRegions] = useState<string[]>([]);
  const [regionsHaveBeenSet, setRegionsHaveBeenSet] = useState(false);

  const onChangeRegions = (newRegions: string[]) => {
    setRegions(newRegions);
    setRegionsHaveBeenSet(true);
  };

  const muscles = useMemo(() => Object.keys(value || {})?.map(Number), [value]);

  useEffect(() => {
    if (!regionsHaveBeenSet && !regions?.length && Object.keys(conditions)?.length) {
      const newRegions = Object.values(conditions).reduce((acc: string[], condition) => {
        if (condition.regions?.length) {
          acc.push(...condition.regions);
        }

        return acc;
      }, []);
      setRegions(newRegions);
    }
  }, [conditions, regions?.length, regionsHaveBeenSet]);

  const handleItemChange = (val: {
    muscle: number;
    status?: string;
    e?: boolean;
    left?: boolean;
    right?: boolean;
  }) => {
    const muscleValue: any = { ...value?.[val.muscle] };

    if (val.status) {
      muscleValue[val.status] = val.e;
    }

    muscleValue.left = val.left || false;
    muscleValue.right = val.right || false;
    muscleValue.touched = true;
    onChange('', '', {
      value: { ...value, [val.muscle]: muscleValue },
    });
  };

  const onChangeMuscles = (newMuscles: number[]) => {
    const newValue = { ...value };
    const existingMuscles = Object.keys(newValue).map(Number);
    const newMusclesAdded = newMuscles.filter(muscle => !existingMuscles.includes(muscle));
    existingMuscles.forEach(key => {
      if (!newMuscles.includes(key)) {
        delete newValue[key];
      }
    });
    newMusclesAdded.forEach(key => {
      newValue[key] = {};
    });
    onChange('', '', { value: newValue });
  };

  const removeItem = (muscle: number) => {
    const newValue = { ...value };
    delete newValue[muscle];
    onChange('', '', { value: newValue });
  };

  const acceptSuggestions = () => {
    const newValue: any = { ...value };
    Object.keys(newValue).forEach(key => {
      if (!MYOFASCIAL_MUSCLES_NOT_AUTO_SELECT[key]) {
        newValue[key] = {
          ...newValue[key],
          tender: true,
          hpn: true,
          tp: true,
          touched: true,
        };
      }
    });
    onChange('', '', { value: newValue });
  };

  const dontShowAcceptSuggestionsButton = useMemo(() => {
    const val = value || {};

    return Object.keys(val).some(key => {
      const muscle = val[key];

      return (
        muscle.tender ||
        muscle.hpn ||
        muscle.tp ||
        muscle.spasm ||
        muscle.weakness ||
        muscle.left ||
        muscle.right
      );
    });
  }, [value]);

  return (
    <div className="flex flex-col">
      {!isFetchingMuscles ? (
        <>
          {!!muscles?.length && (
            <Button
              onClick={acceptSuggestions}
              text="Accept suggestions"
              color={ButtonColors.plainWithBorder}
              className={cn(
                'm-2',
                !dontShowAcceptSuggestionsButton ? 'ring-2 ring-offset-2 ring-primary-600' : ''
              )}
              disabled={dontShowAcceptSuggestionsButton}
            />
          )}
          {!!muscles?.length && (
            <div className="overflow-hidden dark:bg-dark-gray-900">
              <ul className="divide-y divide-gray-300 dark:divide-dark-gray-700 rounded">
                {muscles
                  ?.sort((a, b) => {
                    const muscleA = musclesData?.data.find(m => m.ID === a) as Muscle;
                    const muscleB = musclesData?.data.find(m => m.ID === b) as Muscle;

                    return muscleA?.name?.localeCompare(muscleB?.name);
                  })
                  ?.map((muscle, i) => (
                    <MyofascialItem
                      muscle={musclesData?.data.find(m => m.ID === muscle)}
                      key={muscle}
                      onChange={handleItemChange}
                      value={value?.[muscle] || {}}
                      remove={removeItem.bind(null, muscle)}
                      firstItem={i === 0}
                      showRingAroundInput={
                        !dontShowAcceptSuggestionsButton &&
                        !MYOFASCIAL_MUSCLES_NOT_AUTO_SELECT[muscle]
                      }
                    />
                  ))}
              </ul>
            </div>
          )}
          <div className="p-6 border-t border-gray-300 dark:border-dark-gray-700 bg-white dark:bg-dark-gray-900 rounded-b-lg">
            <MuscleAutoCompleteMulti
              muscles={musclesData?.data || []}
              isFetchingMuscles={isFetchingMuscles}
              onChangeMuscles={onChangeMuscles}
              value={muscles}
              regions={regions}
              setRegions={onChangeRegions}
              disabled={signed}
              hideSelected
              whiteBackground
            />
          </div>
        </>
      ) : (
        <Loading color="text-gray-400 mx-auto m-2" size={10} />
      )}
      {!!close && (
        <div className="flex flex-row justify-end">
          <Button onClick={close} text="Close" color={ButtonColors.plainWithBorder} />
        </div>
      )}
    </div>
  );
};

export default Myofascial;
